import styles from './styles.module.scss';
import { Resource, UpgradeActions, UpgradeStatus, UpgradeType } from '../../types/game-types';
import { useGameDispatch } from '../../store/global-context';
import { ComposableIcon } from '@game-libs/exc-libs/src/composable-icons';
import { useItemCost } from '../../store/hooks/useItemCost';
import { useGeneratorContext } from '../../store/generator-context';
import { humanNumber } from '@game-libs/exc-libs';
import { useTranslation } from 'react-i18next';
import { Icon } from '../../gui/Icon';
import { TreeNodeData } from 'skill-tree/src/models';
import { useUpgradesStore } from '../../store/zustand/useUpgradesStore';

export const UpgradesListItem = ({ skill, parentId }: { skill: TreeNodeData; parentId: string | null }) => {
  const dispatch = useGameDispatch();
  const entityType = useGeneratorContext();
  const upgrades = useUpgradesStore((store) => store.upgrades);
  const upgrade = upgrades[entityType][skill.id as UpgradeType];
  const [cost, , tooExpensive, currenciesWithoutFunds] = useItemCost(upgrade, false);

  const { t } = useTranslation('upgrades', { keyPrefix: 'details.' + upgrade.id });
  const { t: tParent } = useTranslation('upgrades', { keyPrefix: 'details.' + parentId });
  const { t: tUpgrades } = useTranslation('upgrades');
  const { t: tGen } = useTranslation('generators');

  const upgradeStatus = upgrade.status || UpgradeStatus.Hidden;

  const isMaxed = upgrade.purchased >= (upgrade.purchaseLimit || Infinity);
  const isLockedOnDemo = upgrade.isLockedOnDemo || false;
  const isLocked = upgradeStatus.valueOf() === UpgradeStatus.Locked;
  const isPurchaseable = upgrade.status === UpgradeStatus.Unlocked && !tooExpensive && !isLockedOnDemo;
  const isHidden = upgradeStatus.valueOf() === UpgradeStatus.Hidden;
  const isEventTypeUpgrade = upgrade.multiplier === 0 && upgrade.bonusPerOwned === 0;

  if (isHidden) {
    return null;
  }

  const onPurchaseUpgrade = () => {
    if (isMaxed || !isPurchaseable || isLockedOnDemo) {
      return;
    }
    dispatch({ type: UpgradeActions.BUY_UPGRADE, payload: { id: skill.id, entityType } });

    const defaultUnlockAtValue = 3;
    const unlockAmountPurchased = upgrade.purchased + 1 === (skill.subTreeConfig?.unlockAt ?? defaultUnlockAtValue);

    if (upgrade.purchased === 0) {
      dispatch({ type: UpgradeActions.SHOW_UPGRADE, payload: { children: skill.children, entityType } });
    }
    if (unlockAmountPurchased) {
      dispatch({ type: UpgradeActions.UNLOCK_UPGRADE, payload: { children: skill.children, entityType } });
    }
  };

  let upgradeIcon: string[] = [];
  if (isLockedOnDemo) {
    upgradeIcon = ['l_s_lockRed'];
  } else if (isLocked) {
    upgradeIcon = ['l_s_lock'];
  } else {
    upgradeIcon = upgrade.icon || [];
  }

  return (
    <button
      className={`${styles.container} ${styles[upgradeStatus]} ${isMaxed ? styles.isMaxed : ''} ${!isPurchaseable ? styles.isNotPurchaseable : ''}`}
      onClick={onPurchaseUpgrade}
      disabled={isMaxed || !isPurchaseable}
      data-testid={`upgrade-list-item-${skill.id}`}
      type="button"
    >
      <div className={styles.iconWrapper}>
        <ComposableIcon iconConfig={upgradeIcon} />
      </div>

      {isLockedOnDemo && <div className={styles.lockedOnDemoMessage}>{tUpgrades('lockedOnDemo')}</div>}

      {!isLockedOnDemo && isLocked && parentId && (
        <div className={styles.lockedMessage}>{tUpgrades('lockedMessage', { parentSkill: tParent('title') })}</div>
      )}

      {!isLockedOnDemo && !isLocked && (
        <>
          <div className={styles.titleWrapper}>
            <span className={styles.title}>{t('title')}</span>
            {upgrade.purchaseLimit && (
              <span className={styles.count}>
                {upgrade.purchased} / {upgrade.purchaseLimit}
              </span>
            )}
          </div>

          <div className={styles.description}>
            {t('description', {
              type: tGen(entityType, { count: 1 }),
              typePlural: tGen(entityType, { count: 2 }),
              bonus: upgrade.bonusPerOwned,
              current: upgrade.multiplier,
            })}
          </div>

          {!isEventTypeUpgrade && (
            <div className={styles.valueTransition}>
              <span className={styles.before}>
                {t('prefix', { defaultValue: '' })}
                {humanNumber(upgrade.multiplier, 3)}
                {t('suffix', { defaultValue: '' })}
              </span>
              {!isMaxed && (
                <>
                  <span className={styles.arrow}> &gt;&gt; </span>
                  <span className={styles.after}>
                    {t('prefix', { defaultValue: '' })}
                    {humanNumber(upgrade.multiplier + upgrade.bonusPerOwned, 3)}
                    {t('suffix', { defaultValue: '' })}
                  </span>
                </>
              )}
            </div>
          )}

          <div className={styles.costContainer}>
            {isMaxed ? (
              <div className={styles.maxed}>{tUpgrades('max')}</div>
            ) : (
              Object.keys(upgrade.cost).map((currency) => (
                <div
                  key={currency}
                  className={`${styles.cost} ${currenciesWithoutFunds.includes(currency as Resource) ? styles.notEnoughFunds : ''}`}
                >
                  <Icon iconId={`item_cost_${currency}`} />
                  <span>{humanNumber(cost[currency as Resource]!)}</span>
                </div>
              ))
            )}
          </div>
        </>
      )}
    </button>
  );
};
